import { DecimalPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  output,
  input,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { MtTooltipDirective } from '../../Directives/mt-tooltip/mt-tooltip.directive';
import { Task, TaskTypeValue } from '../../Models/user';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'archive-bar',
  templateUrl: './archive-bar.component.html',
  styleUrls: [`./archive-bar.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    SpinnerComponent,
    MtTooltipDirective,
    MatProgressBarModule,
    DecimalPipe,
    DatePipe,
    TranslocoPipe,
  ],
})
export class ArchiveBarComponent implements OnChanges, OnDestroy {
  private router = inject(Router);

  readonly task = input<Task>(undefined);
  readonly closed = output<void>();
  readonly download = output<{
    task: Task;
    onFulfilled: () => void;
  }>();

  expectedDuration: number;
  readonly expectedDurationPercentage = signal<number>(undefined);
  readonly expectedDurationRemaining = signal<number>(undefined);
  intervalId: NodeJS.Timeout;
  readonly taskDownloading = signal(false);
  taskMessage: Record<TaskTypeValue, string> = {
    1: 'import',
    2: 'products',
    3: 'menus',
    4: 'consumers',
    5: 'menus',
    6: 'allergen-analysis',
    7: 'regenerate-menus',
  };
  taskMessageTranslationKey = '';

  ngOnChanges(changes: SimpleChanges) {
    const task = this.task();
    if ('task' in changes && task) {
      if (
        task.task_type === 1 &&
        task.options.ocr &&
        task.options.auto_process === false
      ) {
        this.taskMessageTranslationKey = 'import-ocr';
      } else {
        this.taskMessageTranslationKey = this.taskMessage[task.task_type];
      }
    }
    if (
      'task' in changes &&
      task &&
      task.expected_duration &&
      !task.ready &&
      !task.failed
    ) {
      if (changes.task.previousValue?.id !== task.id) {
        this.expectedDuration = this.expectedDurationToMiliseconds(task);
      }
      const [remaining, percentage] =
        this.calculateCurrentDurationPercentage(task);
      this.expectedDurationRemaining.set(remaining);
      this.expectedDurationPercentage.set(percentage);
      clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        this.expectedDurationRemaining.update((value) => value - 500);
        if (this.expectedDurationPercentage() <= 100)
          this.expectedDurationPercentage.update(
            (value) => value + 100 / (this.expectedDuration / 500),
          );
      }, 500);
    }
  }

  downloadArchive(): void {
    this.taskDownloading.set(true);
    const onFulfilled = () => {
      this.taskDownloading.set(false);
    };
    this.download.emit({
      task: this.task(),
      onFulfilled,
    });
  }

  goToMenu(): void {
    const menuId = this.task()?.options?.menu_id as number;
    if (menuId) {
      this.router.navigate(['/menus', menuId, 1]);
    }
  }

  expectedDurationToMiliseconds(task: Task): number {
    const expectedDurationParts = task.expected_duration.split(':');
    const expectedDurationSeconds =
      parseInt(expectedDurationParts[0]) * 60 * 60 +
      parseInt(expectedDurationParts[1]) * 60 +
      parseFloat(expectedDurationParts[2]);
    return expectedDurationSeconds * 1000;
  }

  calculateCurrentDurationPercentage(task: Task): [number, number] {
    const now = new Date();
    const processingStart = new Date(task.processing_start);
    const currentDuration = now.getTime() - processingStart.getTime();

    const expectedRemaining = this.expectedDuration - currentDuration;
    const expectedRemainingPercentage =
      (currentDuration / this.expectedDuration) * 100;
    return [expectedRemaining, expectedRemainingPercentage];
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
  }
}
